import React, { ChangeEvent, forwardRef, HTMLProps, useEffect, useRef } from "react";

import { bemClassNames, combineRefs, TAB_INDEX_FOCUSABLE_ORDER_EXPLICIT } from "@multiweb/reactUtils";
import { ForwardRefType, OnJavaScriptChange, OptionalTabIndex, TextAlign, TextDirectionality } from "@multiweb/types/react";
import { InputType } from "./types";

const classNames = bemClassNames("input-component");

type CustomInputProps = {
    /** Callback volaný při opuštění pole */
    onChange?: (value: string | undefined) => void;
    /** Hodnota pole */
    value: string | undefined;
    /** Umožňuje nastavit tabindex */
    tabIndex?: OptionalTabIndex | typeof TAB_INDEX_FOCUSABLE_ORDER_EXPLICIT;
    /** Příznak, zda je povolen autocomplete */
    autoComplete?: boolean;
    /** Typ vstupního pole */
    type?: InputType;
    /** Callback volaný při odposlechnutí javascriptové `change` eventy */
    onJavaScriptChange?: OnJavaScriptChange;
    /** Určuje směr textu */
    direction?: TextDirectionality;
    /** Zarovnání textu */
    textAlign?: TextAlign;
};

export type InputProps = Omit<HTMLProps<HTMLInputElement>, keyof CustomInputProps | "ref" | "dir"> & CustomInputProps;

/** Obalovací komponenta základního vstupního pole `<input />` s výchozími hodnotami */
export const Input: ForwardRefType<HTMLInputElement, InputProps> = forwardRef<HTMLInputElement, InputProps>(({
    onChange,
    autoComplete = false,
    onJavaScriptChange,
    textAlign,
    ...restInputProps
}, forwardRef) => {
    const innerRef = useRef<HTMLInputElement>(null);
    const ref = combineRefs(innerRef, forwardRef);

    useEffect(() => {
        const handleOnJavaScriptChange = (e: Event) => {
            if (onJavaScriptChange) {
                onJavaScriptChange(e);
            }
        };

        const innerRefCurrent = innerRef.current;

        if (innerRefCurrent) {
            innerRefCurrent.addEventListener("change", handleOnJavaScriptChange);
        }

        return () => {
            if (innerRefCurrent) {
                innerRefCurrent.removeEventListener("change", handleOnJavaScriptChange);
            }
        };
    }, [onJavaScriptChange]);

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
        if (onChange) {
            onChange(e.target.value);
        }
    };

    return (
        <input
            className={classNames({ "text-align": textAlign })}
            onChange={handleChange}
            autoComplete={autoComplete ? "on" : "off"}
            {...{ ref }}
            {...restInputProps}
        />
    );
});
